import { gql, useQuery } from "@apollo/client";
import { Link, useParams } from "react-router-dom";
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

const STREAM_TEAM_LIVE_SESSION_ANALYTICS_QUERY = gql`
    query StreamTeamLiveSessionAnalytics($sessionId: UUID!) {
        streamTeamLiveSessionAnalytics(sessionId: $sessionId) {
            session {
                id
                streamTeamMember {
                    id
                    player {
                        name
                    }
                }
                startedAt
                endedAt
                streamTitle
                gameName
                peakViewerCount
                lastViewerCount
            }
            viewerSamples {
                id
                viewerCount
                sampledAt
            }
            averageViewerCount
        }
    }
`;

interface ViewerSample {
    id: string;
    viewerCount: number;
    sampledAt: string;
}

interface StreamTeamLiveSessionAnalyticsData {
    streamTeamLiveSessionAnalytics: {
        session: {
            id: string;
            streamTeamMember: {
                id: string;
                player: {
                    name: string;
                };
            };
            startedAt: string;
            endedAt: string | null;
            streamTitle: string;
            gameName: string;
            peakViewerCount: number;
            lastViewerCount: number;
        };
        viewerSamples: ViewerSample[];
        averageViewerCount: number;
    };
}

function isValidViewerSample(
    sample: ViewerSample | null | undefined,
): sample is ViewerSample {
    return Boolean(sample && sample.sampledAt);
}

function StatCard({ label, value }: { label: string; value: string | number }) {
    return (
        <div className="rounded-xl border border-slate-700 bg-slate-800 p-4">
            <p className="text-xs font-semibold tracking-wide text-slate-400 uppercase">
                {label}
            </p>
            <p className="mt-2 text-2xl font-bold text-slate-100">{value}</p>
        </div>
    );
}

export default function StreamTeamLiveSessionAnalyticsPage() {
    const { memberId, sessionId } = useParams<{
        memberId: string;
        sessionId: string;
    }>();

    const { data, loading, error } = useQuery<StreamTeamLiveSessionAnalyticsData>(
        STREAM_TEAM_LIVE_SESSION_ANALYTICS_QUERY,
        {
            variables: { sessionId },
            skip: !sessionId,
            fetchPolicy: "cache-and-network",
        },
    );

    if (loading) {
        return (
            <div className="mx-auto mt-4 w-full px-4">
                <div className="rounded-2xl border border-slate-700 bg-slate-800 p-6">
                    <p className="text-slate-300">Loading stream analytics...</p>
                </div>
            </div>
        );
    }

    if (error || !data?.streamTeamLiveSessionAnalytics) {
        return (
            <div className="mx-auto mt-4 w-full px-4">
                <div className="rounded-2xl border border-red-700 bg-red-900/20 p-6">
                    <p className="text-red-300">Failed to load stream analytics.</p>
                    <Link
                        to={`/longhouse/stream-team/${memberId}`}
                        className="mt-4 inline-block text-sm font-medium text-blue-300 underline"
                    >
                        Back to member analytics
                    </Link>
                </div>
            </div>
        );
    }

    const analytics = data.streamTeamLiveSessionAnalytics;
    const safeViewerSamples = (analytics.viewerSamples || []).filter(
        isValidViewerSample,
    );
    const samples = safeViewerSamples.map((sample) => ({
        ...sample,
        label: new Date(sample.sampledAt).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
        }),
    }));

    return (
        <div className="mx-auto mt-4 w-full px-4">
            <div className="mb-6 rounded-2xl border border-slate-700 bg-slate-800 p-6">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-100">
                            Stream Session Analytics
                        </h1>
                        <p className="mt-2 text-sm text-slate-400">
                            {analytics.session.streamTeamMember.player.name} -{" "}
                            {analytics.session.streamTitle || "Untitled Stream"}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                            {new Date(analytics.session.startedAt).toLocaleString()} -{" "}
                            {analytics.session.endedAt
                                ? new Date(analytics.session.endedAt).toLocaleString()
                                : "LIVE"}
                        </p>
                    </div>
                    <Link
                        to={`/longhouse/stream-team/${memberId}`}
                        className="rounded-lg border border-slate-600 px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:bg-slate-700"
                    >
                        Back
                    </Link>
                </div>
            </div>

            <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <StatCard
                    label="Peak Viewers"
                    value={analytics.session.peakViewerCount.toLocaleString()}
                />
                <StatCard
                    label="Average Viewers"
                    value={analytics.averageViewerCount.toFixed(1)}
                />
                <StatCard
                    label="Latest Viewers"
                    value={analytics.session.lastViewerCount.toLocaleString()}
                />
                <StatCard label="Viewer Samples" value={safeViewerSamples.length} />
            </div>

            <div className="rounded-2xl border border-slate-700 bg-slate-800 p-6">
                <h2 className="mb-4 text-xl font-bold text-slate-100">
                    Viewership Over Time
                </h2>
                {samples.length === 0 ? (
                    <p className="text-sm text-slate-400">
                        No viewer samples captured for this stream yet.
                    </p>
                ) : (
                    <div className="h-90 w-full">
                        <ResponsiveContainer>
                            <LineChart data={samples}>
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke="#334155"
                                />
                                <XAxis
                                    dataKey="label"
                                    stroke="#94a3b8"
                                    minTickGap={24}
                                />
                                <YAxis stroke="#94a3b8" />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: "#0f172a",
                                        border: "1px solid #334155",
                                        borderRadius: "8px",
                                    }}
                                    labelStyle={{ color: "#cbd5e1" }}
                                />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="viewerCount"
                                    name="Viewers"
                                    stroke="#facc15"
                                    strokeWidth={2.5}
                                    dot={false}
                                    activeDot={{ r: 5 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </div>
    );
}
